import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { whatsappApi } from "@services/api/organizations";
import type { WhatsAppTemplate, WhatsAppTemplatePrefill } from "@services/api/organizations";

/**
 * Grande modale de choix d'un message WhatsApp pour une discussion client.
 *
 * Tous les templates de l'organisation sont listés déjà rendus avec les variables
 * pré-remplies par le backend (prénom, véhicule, garage, date du RDV…), une recherche
 * plein texte filtre sur le libellé ET le message rendu, et il ne reste à saisir que
 * les champs que le backend ne connaît pas (motif, montant…).
 *
 * Hors fenêtre 24h seuls les templates approuvés par Meta sont proposés — le parent
 * décide ensuite d'envoyer en texte libre (fenêtre ouverte) ou en template.
 */

interface Props {
  discussionId: string;
  windowOpen: boolean;
  windowHours?: number;
  sending: boolean;
  onSend: (template: WhatsAppTemplate, vars: Record<string, string>) => Promise<boolean>;
  onClose: () => void;
}

const FONT = "Poppins";

/** Remplace {{k}} par la valeur ; une variable vide reste visible comme « [libellé] ». */
function renderBody(t: WhatsAppTemplate, vars: Record<string, string>): string {
  let out = t.body;
  for (const [k, label] of Object.entries(t.variables || {})) {
    const v = (vars[k] || "").trim();
    out = out.split(`{{${k}}}`).join(v || `[${label}]`);
  }
  return out;
}

function normalize(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

function missingKeys(t: WhatsAppTemplate, vars: Record<string, string>): string[] {
  return Object.keys(t.variables || {}).filter((k) => !(vars[k] || "").trim());
}

const APPROVAL_LABEL: Record<string, string> = {
  approved: "Approuvé",
  pending: "En attente Meta",
  rejected: "Refusé",
  error: "Erreur",
  paused: "Suspendu",
};

export default function WhatsAppTemplateModal({ discussionId, windowOpen, windowHours, sending, onSend, onClose }: Props) {
  const [templates, setTemplates] = useState<WhatsAppTemplate[] | null>(null);
  const [prefill, setPrefill] = useState<WhatsAppTemplatePrefill | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Saisies de l'opérateur, par template, par-dessus le pré-remplissage : changer de
  // message puis revenir ne perd pas ce qui a été tapé.
  const [edits, setEdits] = useState<Record<string, Record<string, string>>>({});

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    Promise.all([
      whatsappApi.listTemplates(),
      whatsappApi.prefillTemplates(discussionId).catch(() => null),
    ])
      .then(([list, pre]) => {
        if (cancelled) return;
        setTemplates(list);
        setPrefill(pre);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setTemplates([]);
        setLoadError(err?.response?.data?.detail || "Impossible de charger les templates");
      });
    return () => { cancelled = true; };
  }, [discussionId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const varsFor = (t: WhatsAppTemplate): Record<string, string> => ({
    ...(prefill?.by_template?.[t.id] || {}),
    ...(edits[t.id] || {}),
  });

  const visible = useMemo(() => {
    const base = (templates || [])
      .filter((t) => windowOpen || t.approval_status === "approved")
      .sort((a, b) => (a.friendly_name || a.name).localeCompare(b.friendly_name || b.name, "fr"));
    const q = normalize(search.trim());
    if (!q) return base;
    const words = q.split(/\s+/);
    return base.filter((t) => {
      const hay = normalize([
        t.friendly_name, t.name, t.body, renderBody(t, varsFor(t)), ...(t.quick_replies || []),
      ].join(" "));
      return words.every((w) => hay.includes(w));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates, search, windowOpen, prefill, edits]);

  const selected = visible.find((t) => t.id === selectedId) || null;
  const selectedVars = selected ? varsFor(selected) : {};
  const selectedMissing = selected ? missingKeys(selected, selectedVars) : [];
  const ctx = prefill?.context;
  const contextLine = [ctx?.prenom, ctx?.vehicule].filter(Boolean).join(" · ");

  const handleSend = async () => {
    if (!selected || sending || selectedMissing.length > 0) return;
    const ok = await onSend(selected, selectedVars);
    if (ok) onClose();
  };

  return createPortal(
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 10002,
        background: "rgba(17, 24, 39, 0.45)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
    >
      <div
        role="dialog"
        aria-label="Messages WhatsApp"
        style={{
          width: "min(1040px, 100%)", height: "min(720px, 100%)",
          background: "#FFFFFF", borderRadius: 16,
          boxShadow: "0px 12px 40px rgba(0, 0, 0, 0.25)",
          display: "flex", flexDirection: "column", overflow: "hidden", fontFamily: FONT,
        }}
      >
        {/* En-tête */}
        <div className="flex items-start gap-3 px-5 pt-4 pb-3" style={{ borderBottom: "1px solid #F3F4F6" }}>
          <div className="flex flex-col gap-0.5" style={{ flex: 1, minWidth: 0 }}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-h2 font-semibold" style={{ color: "#111827" }}>Messages WhatsApp</span>
              <span
                className="text-h5 px-2 py-0.5 rounded-full"
                style={{
                  background: windowOpen ? "#F0FDF4" : "#FFFBEB",
                  color: windowOpen ? "#166534" : "#92400E",
                }}
              >
                {windowOpen
                  ? `Fenêtre ouverte${windowHours ? ` (${windowHours}h restantes)` : ""} — envoi en texte libre`
                  : "Fenêtre 24h fermée — envoi en template approuvé"}
              </span>
            </div>
            <span className="text-h4" style={{ color: "#6B7280" }}>
              {contextLine ? `Pré-rempli pour ${contextLine}` : "Les champs connus du dossier sont pré-remplis"}
            </span>
          </div>
          <button
            onClick={onClose}
            className="rounded-md hover:bg-neutral-100 flex items-center justify-center"
            style={{ width: 32, height: 32, flexShrink: 0 }}
            title="Fermer (Échap)"
          >
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth={2} strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Recherche */}
        <div className="px-5 py-3" style={{ borderBottom: "1px solid #F3F4F6" }}>
          <div className="flex items-center gap-2 px-3 rounded-lg" style={{ border: "1px solid #D1D5DB", height: 38 }}>
            <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth={2} strokeLinecap="round">
              <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
            </svg>
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher dans les messages (ex. « facture », « rendez-vous », « devis »)…"
              className="text-h3 flex-1"
              style={{ outline: "none", border: "none", background: "transparent", fontFamily: FONT, minWidth: 0 }}
            />
            {search && (
              <button onClick={() => setSearch("")} className="text-h4" style={{ color: "#9CA3AF" }}>Effacer</button>
            )}
          </div>
        </div>

        {/* Corps : liste + détail */}
        {/* Pas de flex-wrap ici : en multi-ligne, la hauteur d'une ligne flex suit son
            contenu et ignore celle du conteneur → la liste débordait sous la modale
            sans pouvoir défiler. En nowrap, les colonnes prennent la hauteur restante. */}
        <div className="flex" style={{ flex: 1, minHeight: 0 }}>
          <div
            className="flex flex-col"
            style={{ flex: "1 1 45%", minWidth: 0, borderRight: "1px solid #F3F4F6", overflowY: "auto", minHeight: 0 }}
          >
            {templates === null ? (
              <div className="px-5 py-8 text-center text-h3" style={{ color: "#9CA3AF" }}>Chargement…</div>
            ) : loadError ? (
              <div className="px-5 py-8 text-center text-h3" style={{ color: "#B91C1C" }}>{loadError}</div>
            ) : visible.length === 0 ? (
              <div className="px-5 py-8 text-center text-h3" style={{ color: "#9CA3AF" }}>
                {search ? "Aucun message ne correspond à la recherche" : windowOpen ? "Aucun template" : "Aucun template approuvé par Meta"}
              </div>
            ) : (
              visible.map((t) => {
                const vars = varsFor(t);
                const missing = missingKeys(t, vars);
                const active = t.id === selectedId;
                return (
                  <button
                    key={t.id}
                    onClick={() => setSelectedId(t.id)}
                    className="flex flex-col gap-1 w-full text-left px-5 py-3"
                    style={{
                      borderBottom: "1px solid #F9FAFB",
                      background: active ? "#F0FDF4" : "transparent",
                      borderLeft: `3px solid ${active ? "#25D366" : "transparent"}`,
                    }}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-h3 font-medium" style={{ color: "#111827" }}>
                        {t.friendly_name || t.name}
                      </span>
                      {t.approval_status !== "approved" && (
                        <span className="text-h5 px-1.5 py-0.5 rounded-full" style={{ background: "#FFFBEB", color: "#92400E", fontSize: 9 }}>
                          {APPROVAL_LABEL[t.approval_status] || t.approval_status}
                        </span>
                      )}
                      {missing.length > 0 && (
                        <span className="text-h5 px-1.5 py-0.5 rounded-full" style={{ background: "#FEF2F2", color: "#B91C1C", fontSize: 9 }}>
                          {missing.length} champ{missing.length > 1 ? "s" : ""} à compléter
                        </span>
                      )}
                    </div>
                    <span
                      className="text-h4"
                      style={{
                        color: "#6B7280", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden",
                      }}
                    >
                      {renderBody(t, vars)}
                    </span>
                    {t.quick_replies.length > 0 && (
                      <div className="flex gap-1 flex-wrap mt-0.5">
                        {t.quick_replies.map((qr) => (
                          <span key={qr} className="text-h5 px-1.5 py-0.5 rounded-full" style={{ background: "#EFF6FF", color: "#3B82F6", fontSize: 9 }}>
                            {qr}
                          </span>
                        ))}
                      </div>
                    )}
                  </button>
                );
              })
            )}
          </div>

          <div className="flex flex-col" style={{ flex: "1 1 55%", minWidth: 0, minHeight: 0 }}>
            {!selected ? (
              <div className="flex items-center justify-center text-h3 px-6 text-center" style={{ flex: 1, color: "#9CA3AF" }}>
                Sélectionnez un message à gauche pour le vérifier et l'envoyer
              </div>
            ) : (
              <>
                <div className="flex flex-col gap-3 px-5 py-4" style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
                  <span className="text-h3 font-semibold" style={{ color: "#111827" }}>
                    {selected.friendly_name || selected.name}
                  </span>

                  {/* Aperçu façon bulle WhatsApp */}
                  <div
                    className="px-4 py-3 text-h3"
                    style={{ background: "#DCF8C6", borderRadius: "12px 12px 12px 2px", color: "#111827", whiteSpace: "pre-wrap", alignSelf: "flex-start", maxWidth: "92%" }}
                  >
                    {renderBody(selected, selectedVars)}
                  </div>
                  {(selected.buttons?.length > 0 || selected.quick_replies.length > 0) && (
                    <div className="flex gap-1.5 flex-wrap">
                      {selected.buttons?.map((b) => (
                        <span key={b.title} className="text-h4 px-2.5 py-1 rounded-lg" style={{ background: "#FFFFFF", border: "1px solid #D1D5DB", color: "#1D4ED8" }}>
                          ↗ {b.title}
                        </span>
                      ))}
                      {selected.quick_replies.map((qr) => (
                        <span key={qr} className="text-h4 px-2.5 py-1 rounded-lg" style={{ background: "#FFFFFF", border: "1px solid #D1D5DB", color: "#1D4ED8" }}>
                          {qr}
                        </span>
                      ))}
                    </div>
                  )}

                  {Object.keys(selected.variables || {}).length > 0 && (
                    <div className="flex flex-col gap-2 mt-1">
                      <span className="text-h4 font-medium" style={{ color: "#374151" }}>Champs du message</span>
                      {Object.entries(selected.variables).map(([k, label]) => {
                        const value = selectedVars[k] || "";
                        const prefilled = !!prefill?.by_template?.[selected.id]?.[k] && !(edits[selected.id] || {})[k];
                        return (
                          <div key={k} className="flex flex-col gap-0.5">
                            <label className="text-h5" style={{ color: "#6B7280" }}>
                              {label}
                              {prefilled && <span style={{ color: "#16A34A" }}> · pré-rempli</span>}
                              {!value && <span style={{ color: "#B91C1C" }}> · à compléter</span>}
                            </label>
                            <input
                              value={value}
                              onChange={(e) => setEdits((all) => ({ ...all, [selected.id]: { ...(all[selected.id] || {}), [k]: e.target.value } }))}
                              placeholder={label}
                              className="px-2.5 py-1.5 rounded-lg text-h3"
                              style={{ border: `1px solid ${value ? "#D1D5DB" : "#FCA5A5"}`, outline: "none", fontFamily: FONT }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 px-5 py-3" style={{ borderTop: "1px solid #F3F4F6" }}>
                  <span className="text-h4" style={{ color: "#9CA3AF", flex: 1 }}>
                    {selectedMissing.length > 0
                      ? `Complétez ${selectedMissing.length} champ${selectedMissing.length > 1 ? "s" : ""} avant l'envoi`
                      : windowOpen ? "Part en message texte (fenêtre ouverte, sans coût template)" : "Part en template approuvé (~0,05 €)"}
                  </span>
                  <button
                    onClick={onClose}
                    className="text-h3 font-medium px-3 py-2 rounded-lg hover:bg-neutral-100"
                    style={{ color: "#6B7280" }}
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleSend}
                    disabled={sending || selectedMissing.length > 0}
                    className="text-h3 font-medium px-4 py-2 rounded-lg"
                    style={{
                      background: "#25D366", color: "#FFFFFF",
                      opacity: sending || selectedMissing.length > 0 ? 0.5 : 1,
                      cursor: sending || selectedMissing.length > 0 ? "not-allowed" : "pointer",
                    }}
                  >
                    {sending ? "Envoi…" : windowOpen ? "Envoyer" : "Envoyer (template)"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
